import * as React from 'react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import './LayoutHeader.scss'
import { Col, Container, Row } from 'react-bootstrap'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectAuthenticatedUser, selectAuthenticatedUserTokenExpiration } from '../../store/auth/authSlice'
import { useNavigate } from 'react-router-dom'
import { CountdownTimer } from '../../components/CountdownTimer/CountdownTimer'
import PrizeBadge from '../../components/ScopedBlock/PrizeBadge.svg?url'
import { Button } from '../../components/Button/Button'
import { useSessionAvailability, useZocksAuth } from '../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { useGetBillingCustomerQuery } from '../../store/plan/plan'
import { FreeTrialLimitIndicator } from '../../components/Plan/FreeTrialLimitIndicator'
import { HeaderContext } from './HeaderContext'
import { WebNotificationBanner } from '../../components/WebNotifications/WebNotificationBanner'
import { SessionLimitIndicator } from '../../components/Plan/SessionLimitIndicator'
import { ProfileDropdown } from './ProfileDropdown'
import { CrossAccountBanner } from '../../components/CrossAccount/CrossAccountBanner'

export const LayoutHeader: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const user = useAppSelector(selectAuthenticatedUser)
  const tokenExpiration = useAppSelector(selectAuthenticatedUserTokenExpiration)
  const navigate = useNavigate()
  const { hasScope } = useZocksAuth()

  const [showExhaustedLimitsWarning, setExhaustedLimitsWarning] = useState(false)
  const { maxSessionsInPeriod, usedSessionsInPeriod, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()
  const { data: customer } = useGetBillingCustomerQuery({ accountId }, { skip: !hasScope('**', 'billing_customer:read', ['billing_customer:admin']) })

  const { setPortalRef } = useContext(HeaderContext)

  const portalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setPortalRef(portalRef)
  }, [portalRef, setPortalRef])

  const handleFreeTrialClick = () => {
    navigate('/account/plan/plan?period=month')
  }

  const checkFreeTrial = useCallback(() => {
    if (customer) {
      return !!(customer.data.freeTrial && customer.data.freeTrial.startedAt && !customer.data.freeTrial.endedAt)
    }
    return false
  }, [customer])

  useEffect(() => {
    setExhaustedLimitsWarning(false)
    if (sessionAvailabilitySuccess) {
      if (!maxSessionsInPeriod) {
        return
      }
      if (usedSessionsInPeriod >= maxSessionsInPeriod) {
        setExhaustedLimitsWarning(true)
      }
    }
  }, [sessionAvailabilitySuccess, maxSessionsInPeriod, usedSessionsInPeriod])

  return (
    <Container fluid className='p-0 m-0'>
      {showExhaustedLimitsWarning && (
        <Row className='zocks-bg-color-gray-900 zocks-text-white pt-2 pb-2 header-banner'>
          <Col className='d-flex gap-4 justify-content-center align-items-center'>
            <div>You have exhausted your available meetings for the current period.</div>
            <ScopedBlock
              scope={{
                scope: 'billing_subscription:change',
                superScope: ['billing_subscription:admin'],
              }}
              missingScopeAction={MissingScopeAction.HIDE}
            >
              <Button className='free-trial-button' variant='dark' onClick={handleFreeTrialClick}>
                <img alt='Upgrade' src={PrizeBadge} /> Upgrade your plan
              </Button>
            </ScopedBlock>
          </Col>
        </Row>
      )}
      <WebNotificationBanner />
      <CrossAccountBanner />
      {user?.isGhosted && (
        <Row className='zocks-bg-color-danger zocks-text-white pt-2 pb-2'>
          <Col className='d-flex gap-4 justify-content-center align-items-center'>
            <div>
              You are ghosting: {user?.firstName} {user?.lastName}
            </div>
            {tokenExpiration ? (
              <div>
                <CountdownTimer targetDate={tokenExpiration * 1000} />
              </div>
            ) : null}
          </Col>
        </Row>
      )}
      <Row className='zocks-layout-header-row d-flex flex-nowrap position-relative zocks-layout-header-container'>
        {portalRef ? (
          <Col className='p-0'>
            <div ref={portalRef}></div>
          </Col>
        ) : null}
        <Col xs='auto' className='p-0'>
          <div className='d-flex gap-2'>
            <div className='d-none d-lg-flex'>
              {!checkFreeTrial() && <SessionLimitIndicator />}
              <ScopedBlock authActions={[checkFreeTrial]} missingScopeAction={MissingScopeAction.HIDE}>
                <FreeTrialLimitIndicator />
              </ScopedBlock>
            </div>
            <ProfileDropdown />
          </div>
        </Col>
      </Row>
    </Container>
  )
}
